import React, { useEffect, useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faEye, faUsers, faTrash, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from "react-router-dom";

const ListPartners = () => {
  // État pour la liste des partenaires
  const [partners, setPartners] = useState([]);
  // État pour savoir si on affiche la corbeille 
  const [showTrash, setShowTrash] = useState(false);
  // État pour savoir quel partenaire est en cours d’édition
  const [editingPartner, setEditingPartner] = useState(null);
  // État du formulaire d’édition
  const [editForm, setEditForm] = useState({ name: '', logo: '', site: '' });
  // Message de succès
  const [message, setMessage] = useState('');
  // Message d'erreur
  const [error, setError] = useState(null);
  // Hook de navigation (react-router)
  const navigate = useNavigate();

  // Fonction pour récupérer les partenaires (avec ou sans les supprimés)
  const fetchPartners = async (includeDeleted = false) => {
    try {
      const token = localStorage.getItem("token");
      // Si includeDeleted = true → on ajoute le paramètre ?withDeleted=true
      const url = includeDeleted ? '/partner?withDeleted=true' : '/partner';
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Erreur lors du chargement des partenaires");

      const data = await response.json();
      setPartners(data); // On met à jour la liste des partenaires
    } catch (error) {
      console.error(error);
    }
  };

  // Chargement automatique des données à chaque changement de showTrash
  useEffect(() => {
    fetchPartners(showTrash);
  }, [showTrash]);

  // Suppression d'un partenaire (soft delete)
  const deletePartner = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/partner/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Erreur de suppression');
      // Mise à jour locale : on change juste la date de suppression
      setPartners((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, deletedAt: new Date().toISOString() } : p
        )
      );
      //setMessage('Partenaire supprimé');
    } catch (error) {
      console.error("Erreur de suppression :", error);
      setError('Erreur de suppression');
    }
  };

  // Restauration d'un partenaire supprimé
 
const restorePartner = async (id) => {
  try {
    const token = localStorage.getItem("token");
    const response = await fetch(`/partner/restore/${id}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      throw new Error("Non autorisé - Vérifiez votre authentification");
    }

    if (!response.ok) {
      throw new Error("Erreur lors de la restauration");
    }

    const restoredPartner = await response.json();

    setPartners((prev) =>
      prev.map((p) =>
        p.id === id ? { ...restoredPartner } : p
      )
    );

    fetchPartners(showTrash);

    //setMessage('Partenaire restauré avec succès');
  } catch (error) {
    console.error("Erreur restorePartner:", error);
    setError(error.message);
  }
};




  // Filtrer les partenaires affichés selon l'état showTrash
  const displayedPartners = useMemo(() => {
    return partners.filter(p =>
      showTrash
        ? p.deletedAt !== null && p.deletedAt !== '' && p.deletedAt !== undefined // afficher supprimés
        : p.deletedAt === null || p.deletedAt === '' || p.deletedAt === undefined // afficher actifs
    );
  }, [partners, showTrash]);

  // Préparer le formulaire pour l'édition
  const handleEditPartner = (partner) => {
    setEditingPartner(partner);
    setEditForm({ name: partner.name, logo: partner.logo, site: partner.site });
    // Scroll vers le formulaire
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    setMessage('');
    setError(null);
  };

  // Gérer le changement d’un champ du formulaire
  const handleEditFormChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  // Soumettre le formulaire d’édition
  const submitEditForm = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage('');
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/partner/${editingPartner.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editForm),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Erreur de mise à jour');
      }

      // Fermer le formulaire et recharger la liste
      setEditingPartner(null);
      fetchPartners();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="dashboard-container">
      {/* Barre de titre et boutons */}
      <div className="top-bar">
        <h3>{showTrash ? 'Partenaires supprimés' : 'Gestion des partenaires'}</h3>
        <div>
          {/* Bouton pour ajouter un partenaire */}
          <button onClick={() => navigate("/admin/partners/new")} className="bout-new">
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: "8px" }} />
            Nouveau partenaire
          </button>
          {/* Bouton pour afficher ou masquer la corbeille */}
          <button onClick={() => setShowTrash(prev => !prev)} className="bout-trash">
            <FontAwesomeIcon
              icon={showTrash ? faUsers : faTrash}
              style={{ marginRight: '8px' }}
            />
            {showTrash ? 'Liste des partenaires' : 'Corbeille'}
          </button>
        </div>
      </div>

      {/* Messages de feedback */}
      {message && <p style={{ color: 'green' }}>{message}</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}

      {/* Formulaire d’édition */}
      {editingPartner && (
        <form onSubmit={submitEditForm} style={{ marginBottom: '20px' }}>
          <h3>Modifier partenaire : {editingPartner.name}</h3>
          <input
            type="text"
            name="name"
            placeholder="Nom"
            value={editForm.name}
            onChange={handleEditFormChange}
            required
          /><br />
          <input
            type="text"
            name="logo"
            placeholder="Chemin Logo"
            value={editForm.logo}
            onChange={handleEditFormChange}
          /><br />
          <input
            type="text"
            name="site"
            placeholder="Site"
            value={editForm.site}
            onChange={handleEditFormChange}
          /><br />
          <button className="create" type="submit">Valider</button>
          <button
            className="cancel"
            type="button"
            onClick={() => setEditingPartner(null)}
            style={{ marginLeft: '10px' }}
          >
            Annuler
          </button>
        </form>
      )}

      {/* Tableau des partenaires */}
      <table className="partners-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Nom</th>
            <th>Site</th>
            <th>Logo</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {displayedPartners.map((partner) => (
            <tr key={partner.id}>
              <td>{partner.id}</td>
              <td>{partner.name}</td>
              <td>{partner.site}</td>
              <td className="p-2 border">
                {partner.logo ? (
                  <img
  src={`/${partner.logo.replace(/^\/+/, '')}`}
  width="55"
  height="40"
  alt={partner.name}
/>

                ) : (
                  <span>-</span>
                )}
              </td>
              <td>
                {partner.deletedAt ? (
                  // Bouton restaurer
                  <button
                    onClick={() => restorePartner(partner.id)}
                    className="icon-button text-red-600"
                  >
                    <FontAwesomeIcon icon={faUndo} /> 
                  </button>
                ) : (
                  <>
                    {/* Bouton supprimer */}
                    <button
                      onClick={() => deletePartner(partner.id)}
                      className="icon-button text-red-600"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                    {/* Bouton éditer */}
                    {/* Bouton éditer */}
<button
onClick={() => navigate(`/admin/partners/EditPartner/${partner.id}`)}
  className="icon-button text-green-600"
>
  <FontAwesomeIcon icon={faEdit} />
</button>

                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

     
    </div>
  );
};

export default ListPartners;
